import React from "react";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Search, Bell, Menu, Plus, LogOut, User, Settings as SettingsIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useLang } from "../../i18n/LangContext";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex h-10 items-center rounded-xl border border-white/8 bg-white/[0.03] p-1" data-testid="lang-switch" role="group" aria-label="Language">
      {["en", "fr"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`} aria-pressed={lang === l}
          className={`rounded-lg px-2.5 text-[11px] font-semibold uppercase transition-colors ${lang === l ? "bg-brand-red text-white" : "text-muted-foreground hover:text-white"}`}>{l}</button>
      ))}
    </div>
  );
};

const TITLES = {
  overview: "Acquisitions Overview",
  properties: "Properties",
  pipeline: "Deal Pipeline",
  agents: "Agent Network",
  "agent-pipeline": "Agent CRM",
  blackbook: "Black Book",
  insights: "AI Insights",
  settings: "Settings",
};

export default function Topbar({ active, onMenu, onNewDeal }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  return (
    <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-white/[0.06] bg-background/80 px-4 py-3 backdrop-blur-xl sm:px-6">
      <button className="lg:hidden text-muted-foreground" onClick={onMenu}><Menu className="h-5 w-5" /></button>

      <div className="hidden sm:block">
        <h1 className="font-display text-lg font-semibold text-white">{t(TITLES[active])}</h1>
        <p className="text-xs text-muted-foreground">{t("Marrakech · Live pipeline")}</p>
      </div>

      <div className="relative mx-auto hidden w-full max-w-md md:block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder={t("Search properties, agents, deals…")}
          className="h-10 bg-input/50 pl-10 border-white/10 focus-visible:ring-brand-red" />
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-muted-foreground">⌘K</kbd>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <LangSwitch />
        <Button onClick={onNewDeal} className="hidden sm:flex h-10 bg-brand-red hover:bg-brand-redDark text-white">
          <Plus className="h-4 w-4" /> {t("New deal")}
        </Button>
        <button className="relative grid h-10 w-10 place-items-center rounded-xl border border-white/8 bg-white/[0.03] text-muted-foreground hover:text-white transition-colors">
          <Bell className="h-[18px] w-[18px]" />
          <span className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-brand-red ring-2 ring-background" />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2.5 rounded-xl border border-white/8 bg-white/[0.03] p-1 pr-3 hover:bg-white/[0.06] transition-colors">
              <img src={user?.avatar} alt="" className="h-8 w-8 rounded-lg object-cover" />
              <div className="hidden text-left sm:block">
                <p className="text-xs font-medium text-white leading-tight">{user?.name}</p>
                <p className="text-[10px] text-muted-foreground leading-tight">{t(({ super_admin: "Super Admin", admin: "Admin", student: "Student" })[user?.role] || "Member")}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 glass-strong border-white/10">
            <DropdownMenuLabel className="text-muted-foreground">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem className="gap-2 focus:bg-white/5"><User className="h-4 w-4" /> {t("Profile")}</DropdownMenuItem>
            <DropdownMenuItem className="gap-2 focus:bg-white/5"><SettingsIcon className="h-4 w-4" /> {t("Settings")}</DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem onClick={logout} className="gap-2 text-brand-red focus:bg-brand-red/10 focus:text-brand-red">
              <LogOut className="h-4 w-4" /> {t("Sign out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
